import { useState, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlbumGrid,
  CreateAlbumDialog,
  EditAlbumDialog,
  DeleteAlbumDialog,
} from '@/components/albums';
import { useAlbums } from '@/hooks/useAlbums';
import type { AlbumListItem } from '@/lib/api';

export function AlbumsPage() {
  const navigate = useNavigate();
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useAlbums();

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingAlbum, setEditingAlbum] = useState<AlbumListItem | null>(null);
  const [deletingAlbum, setDeletingAlbum] = useState<AlbumListItem | null>(null);

  // Flatten all pages of albums
  const albums = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.data);
  }, [data]);

  const handleCreateSuccess = (albumId: string) => {
    navigate({ to: '/albums/$albumId', params: { albumId } });
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">アルバム</h1>
          <p className="mt-1 text-muted-foreground">写真をアルバムに整理しましょう</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="mr-2 h-4 w-4" />
          新規アルバム
        </Button>
      </div>

      {/* Album Grid */}
      <AlbumGrid
        albums={albums}
        isLoading={isLoading}
        onEdit={setEditingAlbum}
        onDelete={setDeletingAlbum}
      />

      {/* Load More */}
      {hasNextPage && (
        <div className="mt-8 flex justify-center">
          <Button variant="outline" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
            {isFetchingNextPage ? '読み込み中...' : 'さらに読み込む'}
          </Button>
        </div>
      )}

      {/* Dialogs */}
      <CreateAlbumDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onSuccess={handleCreateSuccess}
      />

      <EditAlbumDialog
        album={editingAlbum}
        open={!!editingAlbum}
        onOpenChange={(open) => !open && setEditingAlbum(null)}
      />

      <DeleteAlbumDialog
        album={deletingAlbum}
        open={!!deletingAlbum}
        onOpenChange={(open) => !open && setDeletingAlbum(null)}
      />
    </div>
  );
}
